import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Coins, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ShippingAddressFields } from '@/components/features/products/ShippingAddressFields'
import { emptyShipping, shippingFieldOf, toShippingPayload, validateShipping, type ShippingField, type ShippingForm } from '@/lib/checkout/shipping'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { sdk } from '@/lib/sdk'
import type { PublicProduct } from '@/types/checkout'
import type { RedeemResult } from '@/types/points'
import { usePointsLabel } from './usePointsName'

const newKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? `redeem-${crypto.randomUUID()}` : `redeem-${Date.now()}-${Math.random().toString(36).slice(2)}`

interface RedeemPointsDialogProps {
  product: PublicProduct | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** After a redeem went through (the store reloads balances and stock). */
  onRedeemed?: () => void
}

/**
 * F-160: one unit for points, never through the payment processor. The `Idempotency-Key` is made when
 * the dialog opens and kept while an attempt has no answer (a lost connection retried with the same key
 * answers the same purchase without spending twice); any answer from the server starts a new one.
 */
export function RedeemPointsDialog({ product, open, onOpenChange, onRedeemed }: RedeemPointsDialogProps) {
  const { t } = useTranslation(['points', 'products'])
  const label = usePointsLabel()
  const [key, setKey] = React.useState(newKey)
  const [balance, setBalance] = React.useState<number | null>(null)
  const [shipping, setShipping] = React.useState<ShippingForm>(() => emptyShipping())
  const [shippingErrors, setShippingErrors] = React.useState<Partial<Record<ShippingField, string>>>({})
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState<RedeemResult | null>(null)

  const price = Number(product?.pointsPrice) || 0
  const physical = product?.kind === 'physical'

  React.useEffect(() => {
    if (!open) return
    setKey(newKey()); setError(null); setDone(null); setShippingErrors({}); setShipping(emptyShipping())
    setBalance(null)
    let alive = true
    sdk.points.me({ limit: 1 }).then((s) => { if (alive) setBalance(s.balance) }).catch(() => undefined)
    return () => { alive = false }
  }, [open, product?.id])

  if (!product) return null

  const after = balance === null ? null : balance - price
  const short = after !== null && after < 0

  const submit = async () => {
    if (physical) {
      const errs = validateShipping(shipping)
      setShippingErrors(errs)
      if (Object.keys(errs).length > 0) return
    }
    setSubmitting(true); setError(null)
    try {
      const result = await sdk.points.redeem(product.id, key, physical ? { ...toShippingPayload(shipping) } : undefined)
      setDone(result)
      setBalance(result.balance)
      onRedeemed?.()
    } catch (err) {
      const answered = Boolean((err as { response?: unknown })?.response)
      const { message, param, data } = apiErrorMessage(err)
      if (!answered) {
        /* No answer: the same key may have gone through, so it is kept for the retry. */
        setError(t('redeem.connectionError'))
      } else {
        setKey(newKey())
        const field = shippingFieldOf(param)
        if (physical && field && field !== 'form') setShippingErrors((prev) => ({ ...prev, [field]: message || undefined }))
        /* 400 points.insufficient carries `{ balance, pointsPrice, missing }`: the balance shown catches up. */
        const info = data as { missing?: number; balance?: number } | null
        if (typeof info?.balance === 'number') setBalance(info.balance)
        setError(message || t('redeem.failed'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-1.5rem)] max-w-lg overflow-y-auto p-4 sm:p-6" data-testid="redeem-dialog">
        {done ? (
          <div className="space-y-4 text-center" data-testid="redeem-done">
            <CheckCircle2 className="mx-auto h-10 w-10 text-success-text" />
            <DialogHeader className="text-center">
              <DialogTitle className="text-center">{t('redeem.doneTitle')}</DialogTitle>
              <DialogDescription className="text-center">
                {t('redeem.doneText', { product: product.title, points: label(done.pointsPaid) })}
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-foreground-secondary">{t('redeem.balanceNow', { points: label(done.balance) })}</p>
            <Button variant="secondary" className="w-full" onClick={() => onOpenChange(false)} data-testid="redeem-close">{t('redeem.close')}</Button>
          </div>
        ) : (
          <div className="min-w-0 space-y-4">
            <DialogHeader className="text-left">
              <DialogTitle>{t('redeem.title')}</DialogTitle>
              <DialogDescription>{product.title}</DialogDescription>
            </DialogHeader>
            <dl className="space-y-2 rounded-xl border border-border p-4 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">{t('redeem.cost')}</dt>
                <dd className="flex items-center gap-1.5 font-semibold text-foreground"><Coins className="h-4 w-4" />{label(price)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">{t('redeem.balance')}</dt>
                <dd className="text-foreground" data-testid="redeem-balance">{balance === null ? '—' : label(balance)}</dd>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-border pt-2">
                <dt className="text-muted-foreground">{t('redeem.after')}</dt>
                <dd className={short ? 'font-medium text-danger-text' : 'font-medium text-foreground'}>{after === null ? '—' : label(after)}</dd>
              </div>
            </dl>
            {short && <p className="text-sm text-danger-text" data-testid="redeem-short">{t('redeem.missing', { points: label(price - (balance ?? 0)) })}</p>}
            {physical && (
              <section>
                <h4 className="mb-2 text-xs text-muted-foreground">{t('products:shipping.title')}</h4>
                <ShippingAddressFields
                  value={shipping}
                  errors={shippingErrors}
                  onChange={(field, value) => setShipping((prev) => ({ ...prev, [field]: value }))}
                  disabled={submitting}
                />
              </section>
            )}
            <p className="text-xs text-muted-foreground">{t('redeem.rules')}</p>
            {error && <p role="alert" className="text-sm text-danger-text" data-testid="redeem-error">{error}</p>}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('redeem.cancel')}</Button>
              <Button variant="secondary" onClick={() => void submit()} disabled={submitting || short} data-testid="redeem-confirm">
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('redeem.confirm', { points: label(price) })}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
